import { Collection, Database, Q } from '@nozbe/watermelondb';
import { ContactModel, ContactPayload } from '../../../../types/Contacts';
import { EventContactModel } from '../../../../types/EventContact';
import { HorseContactModel } from '../../../../types/HorseContact';
import { HorseModel } from '../../../../types/Horses';
import { LocationModel } from '../../../../types/Location';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { capitalizeWords, stripSpecialChars } from '../../../../utils/string';
import { LoggerFunction } from '../../../../types/logger';
import { ContactPostEditPayload } from '../../../../types/Contacts';
import { getLocationIdToAssign, getUpdatedFieldValue } from '../../../utils';

export const prepareUpdateContact = ({
    contactElement,
    payload,
    locationElement,
    fallbackLocationExists,
    logError,
    entityName,
}: {
    contactElement: ContactModel;
    payload: Partial<ContactPayload>;
    locationElement?: LocationModel;
    fallbackLocationExists: boolean;
    logError: LoggerFunction;
    entityName: string;
}) =>
    contactElement.prepareUpdate((contact) => {
        contact.firstName =
            capitalizeWords(payload.firstName) || contactElement.firstName;
        contact.lastName = getUpdatedFieldValue(
            payload.lastName,
            contactElement.lastName,
            capitalizeWords,
        );
        contact.description = getUpdatedFieldValue(
            payload.description,
            contactElement.description,
        );
        contact.role = getUpdatedFieldValue(payload.role, contactElement.role);
        contact.phone = getUpdatedFieldValue(
            payload.phone,
            contactElement.phone,
            stripSpecialChars,
        );
        contact.phonePrefix = getUpdatedFieldValue(
            payload.phonePrefix,
            contactElement.phonePrefix,
        );
        contact.email = getUpdatedFieldValue(
            payload.email,
            contactElement.email,
        );
        contact.businessName = getUpdatedFieldValue(
            payload.businessName,
            contactElement.businessName,
        );
        contact.address = getUpdatedFieldValue(
            payload.address,
            contactElement.address,
        );
        contact.comments = getUpdatedFieldValue(
            payload.comments,
            contactElement.comments,
        );
        contact.locationId = getLocationIdToAssign({
            location: payload.location,
            locationElementId: locationElement?.id,
            address: payload.address,
            fallbackLocationId: contactElement.locationId,
            logError,
            entityName,
            fallbackLocationExists,
        });
        contact.billable =
            payload.billable === undefined
                ? contactElement.billable
                : payload.billable;
        contact.hidden =
            payload.hidden === undefined
                ? contactElement.hidden
                : payload.hidden;
        contact.userId = contactElement.userId;
        contact.organisationId = contactElement.organisationId;
    });

export const prepareUpdateEventContact = async ({
    database,
    contact,
    payload,
}: {
    database: Database;
    contact: ContactModel;
    payload?: ContactPostEditPayload;
}): Promise<EventContactModel | undefined> => {
    if (!payload?.eventContact) return undefined;

    const { eventId } = payload.eventContact;
    const [eventContact] = await database.collections
        .get<EventContactModel>('event_contacts')
        .query(
            Q.and(
                Q.where('contact_id', contact.id),
                Q.where('event_id', eventId),
            ),
        );
    if (!eventContact) return undefined;

    return eventContact.prepareUpdate((eventContactElement) => {
        const eventContactPayload = payload.eventContact!;
        Object.keys(eventContactPayload).forEach((key) => {
            eventContactElement[key] = eventContactPayload[key];
        });
    });
};

export const prepareUpdateHorseContacts = ({
    horses,
    initialHorses,
    horseContacts,
    contactElement,
    horseContactsCollection,
}: {
    horses?: ContactPayload['horses'];
    initialHorses: HorseModel[];
    horseContacts: HorseContactModel[];
    contactElement: ContactModel;
    horseContactsCollection: Collection<HorseContactModel>;
}) => {
    const horseContactsToAdd: HorseContactModel[] = [];
    const horseContactsToDelete: HorseContactModel[] = [];

    if (!horses) {
        return { horseContactsToAdd, horseContactsToDelete };
    }

    horses.forEach((horse) => {
        if (
            !initialHorses.find((initialHorse) => initialHorse.id === horse.id)
        ) {
            horseContactsToAdd.push(
                horseContactsCollection.prepareCreate((horseContact) => {
                    horseContact.contactId = contactElement.id;
                    horseContact.userId = contactElement.userId;
                    horseContact.organisationId = contactElement.organisationId;
                    horseContact.horseId = horse.id;
                }),
            );
        }
    });

    horseContacts.forEach((horseContact) => {
        if (!horses.find(({ id }) => id === horseContact.horseId)) {
            horseContactsToDelete.push(horseContact.prepareMarkAsDeleted());
        }
    });

    return { horseContactsToAdd, horseContactsToDelete };
};

export const logUpdateContactActions = ({
    locationElement,
    contactElement,
    horseContactsToAdd,
    horseContactsToDelete,
    logDBAction,
    tableName,
}: {
    locationElement?: LocationModel;
    contactElement: ContactModel;
    horseContactsToAdd: HorseContactModel[];
    horseContactsToDelete: HorseContactModel[];
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    if (locationElement) {
        logDBAction({
            message: 'Update contact - create location',
            modelName: tableName,
            payload: locationElement,
        });
    }
    logDBAction({
        message: 'Update contact',
        modelName: tableName,
        payload: contactElement,
    });
    logDBAction({
        message: 'Update contact - create horse contacts',
        modelName: tableName,
        payload: horseContactsToAdd,
    });
    logDBAction({
        message: 'Update contact - delete horse contacts',
        modelName: tableName,
        payload: horseContactsToDelete,
    });
};
